import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export interface BillingPeriod {
  startDate: Date;
  endDate: Date;
  label: string;
  key: string;
}

interface BillingPeriodSelectorProps {
  value: BillingPeriod;
  onChange: (period: BillingPeriod) => void;
}

function getMonthPeriods(locale: string): BillingPeriod[] {
  const periods: BillingPeriod[] = [];
  const now = new Date();
  const localeCode = locale === 'pt-BR' ? 'pt-BR' : 'en-US';

  // Current month
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  periods.push({
    startDate: currentMonthStart,
    endDate: now, // End at today for current month
    label: currentMonthStart.toLocaleDateString(localeCode, { month: 'long', year: 'numeric' }),
    key: 'current',
  });

  // Previous 5 months
  for (let i = 1; i <= 5; i++) {
    const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59, 999);
    periods.push({
      startDate: monthStart,
      endDate: monthEnd,
      label: monthStart.toLocaleDateString(localeCode, { month: 'long', year: 'numeric' }),
      key: `month-${i}`,
    });
  }

  return periods;
}

export function getCurrentMonthPeriod(locale: string = 'pt-BR'): BillingPeriod {
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const localeCode = locale === 'pt-BR' ? 'pt-BR' : 'en-US';
  return {
    startDate: currentMonthStart,
    endDate: now,
    label: currentMonthStart.toLocaleDateString(localeCode, { month: 'long', year: 'numeric' }),
    key: 'current',
  };
}

export default function BillingPeriodSelector({ value, onChange }: BillingPeriodSelectorProps) {
  const { t, i18n } = useTranslation('billing');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const locale = i18n.language || 'pt-BR';
  const localeCode = locale === 'pt-BR' ? 'pt-BR' : 'en-US';
  const periods = getMonthPeriods(locale);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (period: BillingPeriod) => {
    onChange(period);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <CalendarIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {value.label}
        </span>
        <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
          <div className="py-1">
            {periods.map((period) => (
              <button
                key={period.key}
                onClick={() => handleSelect(period)}
                className={`w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  value.key === period.key ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${
                    value.key === period.key
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {period.label}
                  </span>
                  {period.key === 'current' && (
                    <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                      {t('currentPeriod') || 'Current'}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {period.startDate.toLocaleDateString(localeCode)} - {period.endDate.toLocaleDateString(localeCode)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
